import React, { useState } from 'react';
import {
    Button, Card, DatePicker, Dropdown, Form, Input, InputNumber, Select, Space, Switch, Typography, Upload, notification,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { ChallengesProxy } from '../services/catalogServiceAPI/challengesProxy';
import { ASSETS_BASE_URL, validateImageUrl } from '../utils/validateImageUrl';
import { CampaignAvatarItemSelect } from './CampaignAvatarItemSelect';
import {
    BLOCK_LABELS, BLOCK_TYPES, EMOJI_HELP, ICON_HELP, IMAGE_ASPECT_OPTIONS, MAX_BLOCKS, SPACER_SIZE_OPTIONS, TEXT_ALIGN_OPTIONS,
    DATE_FORMAT, newBlock,
} from './constants';

const { Text } = Typography;
const { TextArea } = Input;

const challengesProxy = new ChallengesProxy();

const MD_HELP = 'Inline markdown: **bold**, *italic*, [link](https://…). Line breaks come from separate blocks.';

function ImageBlockFields({ block, patch }) {
    const [uploading, setUploading] = useState(false);
    const urlError = validateImageUrl(block.url);

    const upload = async (file) => {
        setUploading(true);
        try {
            const { url } = await challengesProxy.uploadImage(file);
            patch({ url });
        } catch (e) {
            notification.error({ message: 'Upload failed', description: e.message });
        } finally {
            setUploading(false);
        }
        return false;
    };

    return (
        <>
            <Form.Item
                label="Image URL"
                required
                validateStatus={urlError ? 'error' : ''}
                help={urlError || `Must be hosted under ${ASSETS_BASE_URL}; upload puts it there.`}
            >
                <Space.Compact style={{ width: '100%' }}>
                    <Input value={block.url} onChange={(e) => patch({ url: e.target.value })} placeholder={`${ASSETS_BASE_URL}/…`} />
                    <Upload beforeUpload={upload} showUploadList={false} accept="image/*">
                        <Button icon={<UploadOutlined />} loading={uploading}>Upload</Button>
                    </Upload>
                </Space.Compact>
            </Form.Item>
            {block.url && !urlError && (
                <img src={block.url} alt="" style={{ maxWidth: 240, maxHeight: 120, borderRadius: 6, marginBottom: 12, display: 'block' }} />
            )}
            <Space align="start" size={16} wrap>
                <Form.Item label="Alt text">
                    <Input value={block.alt ?? ''} maxLength={200} onChange={(e) => patch({ alt: e.target.value })} style={{ width: 260 }} />
                </Form.Item>
                <Form.Item label="Shape" help="Original keeps the image's own proportions.">
                    <Select value={block.aspect ?? 'wide'} options={IMAGE_ASPECT_OPTIONS} onChange={(aspect) => patch({ aspect })} style={{ width: 140 }} />
                </Form.Item>
                <Form.Item label="Width" help="Share of the card, centred.">
                    <InputNumber min={20} max={100} step={5} value={block.width ?? 100} onChange={(width) => patch({ width: width == null || width >= 100 ? null : width })} addonAfter="%" style={{ width: 120 }} />
                </Form.Item>
                <Form.Item label="Transparent" help="For cut-out artwork: no backdrop, the whole image shown.">
                    <Switch checked={block.transparent === true} onChange={(transparent) => patch({ transparent: transparent || null })} />
                </Form.Item>
            </Space>
        </>
    );
}

function BulletsFields({ block, patch }) {
    const items = block.items ?? [];
    const setItem = (index, itemPatch) => patch({ items: items.map((item, i) => (i === index ? { ...item, ...itemPatch } : item)) });
    return (
        <Form.Item label="Items" help={MD_HELP}>
            <Space direction="vertical" style={{ width: '100%' }}>
                {items.map((item, index) => (
                    <Space.Compact key={index} style={{ width: '100%' }}>
                        <Input
                            value={item.icon ?? ''}
                            onChange={(e) => setItem(index, { icon: e.target.value })}
                            placeholder="Icon"
                            style={{ width: 120 }}
                        />
                        <Input
                            value={item.emoji ?? ''}
                            maxLength={16}
                            onChange={(e) => setItem(index, { emoji: e.target.value })}
                            placeholder="Emoji"
                            style={{ width: 70 }}
                        />
                        <Input value={item.md} maxLength={300} onChange={(e) => setItem(index, { md: e.target.value })} placeholder="Text" />
                        <Button
                            icon={<DeleteOutlined />}
                            disabled={items.length <= 1}
                            onClick={() => patch({ items: items.filter((_, i) => i !== index) })}
                        />
                    </Space.Compact>
                ))}
                {items.length < 10 && (
                    <Button size="small" icon={<PlusOutlined />} onClick={() => patch({ items: [...items, { icon: '', md: '' }] })}>Add item</Button>
                )}
            </Space>
        </Form.Item>
    );
}

function BlockFields({ block, patch }) {
    switch (block.type) {
        case 'image':
            return <ImageBlockFields block={block} patch={patch} />;
        case 'icon':
            return (
                <Space size={16} wrap align="start">
                    <Form.Item label="Icon" help={ICON_HELP}>
                        <Input value={block.token ?? ''} maxLength={64} onChange={(e) => patch({ token: e.target.value })} style={{ width: 220 }} />
                    </Form.Item>
                    <Form.Item label="Emoji" help={EMOJI_HELP}>
                        <Input value={block.emoji ?? ''} maxLength={16} onChange={(e) => patch({ emoji: e.target.value })} placeholder="⭐" style={{ width: 90, fontSize: 18 }} />
                    </Form.Item>
                </Space>
            );
        case 'eyebrow':
            return (
                <Form.Item label="Text" required help="A short label above the heading, drawn in the accent color.">
                    <Input value={block.text} maxLength={60} showCount onChange={(e) => patch({ text: e.target.value })} />
                </Form.Item>
            );
        case 'heading':
            return (
                <Form.Item label="Text" required>
                    <Input value={block.text} maxLength={120} showCount onChange={(e) => patch({ text: e.target.value })} />
                </Form.Item>
            );
        case 'text':
            return (
                <>
                    <Form.Item label="Text" required help={MD_HELP}>
                        <TextArea value={block.md} rows={3} maxLength={1500} showCount onChange={(e) => patch({ md: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Alignment">
                        <Select value={block.align ?? 'center'} options={TEXT_ALIGN_OPTIONS} onChange={(align) => patch({ align })} style={{ width: 140 }} />
                    </Form.Item>
                </>
            );
        case 'bullets':
            return <BulletsFields block={block} patch={patch} />;
        case 'reward':
            return (
                <>
                    <Space size={16} wrap>
                        <Form.Item label="Coins">
                            <InputNumber min={0} value={block.coins ?? null} onChange={(coins) => patch({ coins })} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item label="Gems">
                            <InputNumber min={0} value={block.gems ?? null} onChange={(gems) => patch({ gems })} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item label="Label">
                            <Input value={block.label ?? ''} maxLength={80} onChange={(e) => patch({ label: e.target.value })} placeholder="Your gift" style={{ width: 220 }} />
                        </Form.Item>
                    </Space>
                    <Form.Item label="Avatar item" help="The block shows what the user gets; it does not grant anything by itself.">
                        <CampaignAvatarItemSelect value={block.avatarItemId} onChange={(avatarItemId) => patch({ avatarItemId })} />
                    </Form.Item>
                </>
            );
        case 'countdown':
            return (
                <Space size={16} wrap align="start">
                    <Form.Item label="Counts down to (your local time)" required>
                        <DatePicker
                            showTime
                            format={DATE_FORMAT}
                            value={block.until ? dayjs(block.until) : null}
                            onChange={(d) => patch({ until: d ? d.toISOString() : null })}
                        />
                    </Form.Item>
                    <Form.Item label="Label">
                        <Input value={block.label ?? ''} maxLength={80} onChange={(e) => patch({ label: e.target.value })} placeholder="Offer ends in" style={{ width: 220 }} />
                    </Form.Item>
                </Space>
            );
        case 'avatar_items':
            return (
                <Form.Item label="Items" required help="Up to 8, drawn in a row.">
                    <CampaignAvatarItemSelect multiple maxCount={8} value={block.ids} onChange={(ids) => patch({ ids })} />
                </Form.Item>
            );
        case 'spacer':
            return (
                <Form.Item label="Size">
                    <Select value={block.size ?? 'md'} options={SPACER_SIZE_OPTIONS} onChange={(size) => patch({ size })} style={{ width: 140 }} />
                </Form.Item>
            );
        case 'sale':
            return (
                <Form.Item label="Label" help="Draws the campaign's sale (step one) the way the wallet shows it: the discount, the sale price on the web, and when it ends. The preview shows placeholder figures.">
                    <Input value={block.label ?? ''} maxLength={80} onChange={(e) => patch({ label: e.target.value })} placeholder="on Premium" style={{ width: 220 }} />
                </Form.Item>
            );
        case 'store_buttons':
            return (
                <Text type="secondary">
                    The App Store and Google Play buttons: the viewer&apos;s own store on a phone, both on a computer. Website only (the apps draw nothing).
                    A tap is the campaign&apos;s CTA, opens the store and counts as an ads conversion.
                </Text>
            );
        default:
            return <Text type="danger">Unknown block type "{block.type}"</Text>;
    }
}

export function BlocksEditor({ blocks, onChange }) {
    const setBlock = (index, patch) => onChange(blocks.map((b, i) => (i === index ? { ...b, ...patch } : b)));
    const move = (index, delta) => {
        const target = index + delta;
        if (target < 0 || target >= blocks.length) return;
        const next = blocks.slice();
        [next[index], next[target]] = [next[target], next[index]];
        onChange(next);
    };

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            {blocks.length === 0 && <Text type="secondary">No blocks yet. A dialog needs at least one.</Text>}
            {blocks.map((block, index) => (
                <Card
                    key={index}
                    size="small"
                    title={<Text>{index + 1}. {BLOCK_LABELS[block.type] ?? block.type}</Text>}
                    extra={(
                        <Space size={4}>
                            <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={index === 0} onClick={() => move(index, -1)} title="Move up" />
                            <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={index === blocks.length - 1} onClick={() => move(index, 1)} title="Move down" />
                            <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(blocks.filter((_, i) => i !== index))} title="Remove" />
                        </Space>
                    )}
                >
                    <BlockFields block={block} patch={(patch) => setBlock(index, patch)} />
                </Card>
            ))}
            <Dropdown
                disabled={blocks.length >= MAX_BLOCKS}
                menu={{
                    items: BLOCK_TYPES.map((b) => ({ key: b.type, label: b.label })),
                    onClick: ({ key }) => onChange([...blocks, newBlock(key)]),
                }}
            >
                <Button icon={<PlusOutlined />}>Add block</Button>
            </Dropdown>
            {blocks.length >= MAX_BLOCKS && <Text type="secondary">At most {MAX_BLOCKS} blocks.</Text>}
        </Space>
    );
}
